import React, { useState, useEffect } from 'react';
import {
    Box,
    Flex,
    Grid,
    HStack,
    VStack,
    Text,
    Spinner,
    Center,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Tag,
} from '@chakra-ui/react';
import { FiBarChart2, FiMessageCircle, FiActivity, FiUsers } from 'react-icons/fi';
import Header from '../../components/Header';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
import { MonthPicker } from '../../components/MonthPicker';
import API from '../../helpers/axiosInstance';
import moment from 'moment';
import { getUserTimeZone } from '../../utils/DefaultFunction';

const getApiHeaders = () => ({
    'x-api-key': localStorage.getItem('api-key'),
    'x-access-token': localStorage.getItem('customer-portal'),
});

const CHANNEL_COLORS = {
    SMS:      { bg: 'var(--brand-tint)', color: 'var(--brand-primary)', border: 'var(--brand-border)' },
    Whatsapp: { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
    Email:    { bg: '#fdf4ff', color: '#9333ea', border: '#e9d5ff' },
    Push:     { bg: '#fff7ed', color: '#ea580c', border: '#fed7aa' },
    Default:  { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' },
};

const getChannelStyle = (channel) => CHANNEL_COLORS[channel] || CHANNEL_COLORS.Default;

function StatCard({ label, value, sub, icon: Icon, iconBg, iconColor }) {
    return (
        <Box className="box-style-card">
            <Flex justify="space-between" align="flex-start">
                <Box>
                    <Text fontSize="xs" color="gray.500" fontWeight="500" mb={1}>{label}</Text>
                    <Text fontSize="2xl" fontWeight="600" color="gray.800" lineHeight="1">
                        {value?.toLocaleString() ?? '—'}
                    </Text>
                    {sub && <Text fontSize="xs" color="gray.400" mt={1}>{sub}</Text>}
                </Box>
                <Flex
                    align="center" justify="center"
                    w="38px" h="38px" borderRadius="lg"
                    bg={iconBg} color={iconColor}
                    fontSize="lg" flexShrink={0}
                >
                    <Icon />
                </Flex>
            </Flex>
        </Box>
    );
}

function ChannelRow({ row }) {
    const style = getChannelStyle(row.channel);
    const chargeablePct = row.total > 0 ? Math.round((row.chargeable / row.total) * 100) : 0;

    return (
        <>
            <Tr _hover={{ bg: 'gray.50' }}>
                <Td>
                    <Tag
                        size="sm"
                        borderRadius="full"
                        bg={style.bg}
                        color={style.color}
                        border="1px solid"
                        borderColor={style.border}
                        fontWeight="500"
                    >
                        {row.channel}
                    </Tag>
                </Td>
                <Td isNumeric fontWeight="500">{row.total?.toLocaleString()}</Td>
                <Td isNumeric>
                    <HStack justify="flex-end" spacing={2}>
                        <Text fontWeight="500" color="#16a34a">{row.chargeable?.toLocaleString()}</Text>
                        <Tag size="sm" bg="#dcfce7" color="#16a34a" borderRadius="full">{chargeablePct}%</Tag>
                    </HStack>
                </Td>
                <Td isNumeric>
                    <Text color="#dc2626" fontWeight="500">{row.not_chargeable?.toLocaleString()}</Text>
                </Td>
            </Tr>

            {row.message_types?.map((mt, i) => (
                <Tr key={mt.message_type_id ?? i} bg="gray.50">
                    <Td pl={10}>
                        <Text fontSize="sm" color="gray.500">↳ {mt.message_type ?? '—'}</Text>
                    </Td>
                    <Td isNumeric fontSize="sm" color="gray.700">{mt.total?.toLocaleString()}</Td>
                    <Td isNumeric fontSize="sm" color="#16a34a">{mt.chargeable?.toLocaleString()}</Td>
                    <Td isNumeric fontSize="sm" color="#dc2626">{mt.not_chargeable?.toLocaleString()}</Td>
                </Tr>
            ))}
        </>
    );
}

export default function UsageReport({ header }) {
    const [month, setMonth]     = useState(moment());
    const [data, setData]       = useState([]);
    const [mau, setMau]         = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetched, setFetched] = useState(false);

    const startDate = month.clone().startOf('month').format('YYYY-MM-DD');
    const endDate   = month.clone().endOf('month').format('YYYY-MM-DD');

    const totalMessages   = data.reduce((s, r) => s + (r.total || 0), 0);
    const totalChargeable = data.reduce((s, r) => s + (r.chargeable || 0), 0);
    const totalFree       = data.reduce((s, r) => s + (r.not_chargeable || 0), 0);

    const disableFutureMonths = (m) => m.isAfter(moment(), 'month');

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // start_date / end_date are the user's LOCAL month boundaries, so the
                // backend needs the zone to bucket rows the same way — without it the
                // range is read in the server's zone and days at either edge land in the
                // wrong month. `tz` is the key the other reporting endpoints use
                // (msg-log/list, contacts/*/engagement, events list).
                //
                // URLSearchParams, not string interpolation: a zone name like
                // "Asia/Karachi" contains a slash, and the raw-interpolation call sites
                // elsewhere send it unencoded.
                const params = new URLSearchParams({
                    start_date: startDate,
                    end_date: endDate,
                    tz: getUserTimeZone(),
                });
                const res = await API.get(
                    `${process.env.REACT_APP_API_URL}/billing/usage?${params.toString()}`,
                    { headers: getApiHeaders() }
                );
                setData(res.data?.data || []);
                setMau(res.data?.mau ?? null);
                setFetched(true);
            } catch (e) {
                console.error('Failed to fetch usage report:', e);
                setData([]);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [startDate, endDate]);

    return (
        <>
            <Header header={header} />
            <BoxWrapper>

                {/* Page header */}
                <Flex justify="space-between" align="center" mb={6}>
                    <VStack align="start" spacing={0}>
                        <Text fontSize="2xl" fontWeight="500" color="gray.700">Usage Report</Text>
                        <Text fontSize="sm" color="#64748b">
                            Breakdown of message usage by channel and type
                        </Text>
                    </VStack>
                    <MonthPicker
                        value={month}
                        onChange={setMonth}
                        disabledDates={disableFutureMonths}
                    />
                </Flex>

                {loading && (
                    <Center py={16}>
                        <Spinner size="xl" color="blue.500" thickness="4px" />
                    </Center>
                )}

                {!loading && fetched && (
                    <>
                        {/* KPI cards */}
                        <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={4} mb={6}>
                            <StatCard
                                label="Total Messages"
                                value={totalMessages}
                                sub="All channels combined"
                                icon={FiMessageCircle}
                                iconBg="var(--brand-tint)"
                                iconColor="var(--brand-primary)"
                            />
                            <StatCard
                                label="Chargeable"
                                value={totalChargeable}
                                sub={`${totalMessages > 0 ? Math.round((totalChargeable / totalMessages) * 100) : 0}% of total`}
                                icon={FiBarChart2}
                                iconBg="#f0fdf4"
                                iconColor="#16a34a"
                            />
                            <StatCard
                                label="Not Chargeable"
                                value={totalFree}
                                sub={`${totalMessages > 0 ? Math.round((totalFree / totalMessages) * 100) : 0}% of total`}
                                icon={FiActivity}
                                iconBg="#fef2f2"
                                iconColor="#dc2626"
                            />
                            <StatCard
                                label="Monthly Active Users"
                                value={mau}
                                sub="Unique active users this month"
                                icon={FiUsers}
                                iconBg="#f5f3ff"
                                iconColor="#7c3aed"
                            />
                        </Grid>

                        {/* Table */}
                        <Box className="box-style-card">
                            <Flex justify="space-between" align="center" mb={4}>
                                <VStack align="start" spacing={0}>
                                    <Text fontSize="md" fontWeight="500" color="gray.700">Channel Breakdown</Text>
                                    <Text fontSize="xs" color="gray.500">
                                        Breakdown by channel and message type
                                    </Text>
                                </VStack>
                                <Text fontSize="xs" color="gray.400">{startDate} → {endDate}</Text>
                            </Flex>

                            {data.length === 0 ? (
                                <Center py={10}>
                                    <Text color="gray.400" fontSize="sm">No usage data for the selected period.</Text>
                                </Center>
                            ) : (
                                <TableContainer>
                                    <Table variant="simple" size="sm">
                                        <Thead bg="gray.50">
                                            <Tr>
                                                <Th fontSize="xs" color="gray.600" py={3}>Channel</Th>
                                                <Th fontSize="xs" color="gray.600" py={3} isNumeric>Total</Th>
                                                <Th fontSize="xs" color="gray.600" py={3} isNumeric>Chargeable</Th>
                                                <Th fontSize="xs" color="gray.600" py={3} isNumeric>Not Chargeable</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {data.map(row => (
                                                <ChannelRow key={row.channel_id} row={row} />
                                            ))}
                                            {/* Totals row */}
                                            <Tr bg="gray.50">
                                                <Td>
                                                    <Tag
                                                        size="sm"
                                                        borderRadius="full"
                                                        bg="#1e293b"
                                                        color="white"
                                                        fontWeight="500"
                                                    >
                                                        Total
                                                    </Tag>
                                                </Td>
                                                <Td isNumeric fontSize="sm" fontWeight="600">{totalMessages.toLocaleString()}</Td>
                                                <Td isNumeric fontSize="sm" fontWeight="600" color="#16a34a">{totalChargeable.toLocaleString()}</Td>
                                                <Td isNumeric fontSize="sm" fontWeight="600" color="#dc2626">{totalFree.toLocaleString()}</Td>
                                            </Tr>
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    </>
                )}

            </BoxWrapper>
        </>
    );
}
